import { FikoThreeJsComponent } from "../../index";

/**
 * 🔥 WEB WORKERS POOL - Parallel geometry processing
 * Distributes heavy computations across 4 worker threads
 * - STL/DXF parsing in background
 * - Geometry optimization in parallel
 * - Zero UI blocking
 */
export class FikoWorkerPool {
  private static instance: FikoWorkerPool;
  private workers: Worker[] = [];
  private taskQueue: WorkerTask[] = [];
  private activeWorkers = new Map<number, WorkerTask>();
  private taskId = 0;
  // 🚀 PERFORMANS OPT: Dynamic worker count based on CPU cores
  private readonly WORKER_COUNT = Math.max(
    2,
    Math.min(navigator.hardwareConcurrency - 1, 8)
  );

  // 🚀 PERFORMANS OPT: Task timeout to prevent memory leaks
  private readonly TASK_TIMEOUT_MS = 30000; // 30 seconds
  private taskTimeouts = new Map<number, ReturnType<typeof setTimeout>>();

  // 🚀 PERFORMANS OPT: Priority queues for different task priorities
  private priorityQueues: WorkerTask[][] = [[], [], [], []]; // CRITICAL, HIGH, NORMAL, LOW

  // DEN-4 FIX: Max retries per task to prevent poisoned task infinite restart
  private readonly MAX_TASK_RETRIES = 3;

  // Dead worker detection & auto-restart
  private readonly HEARTBEAT_TIMEOUT_MS = 5000;
  private readonly HEALTH_CHECK_INTERVAL_MS = 30000;
  private healthCheckTimer: ReturnType<typeof setInterval> | null = null;
  private pendingPings = new Map<number, { pingId: number; timer: ReturnType<typeof setTimeout> }>();
  private deadWorkerSet = new Set<number>();
  private restartCount = 0;

  private constructor() {
    this.initializeWorkers();
    this.startPeriodicHealthCheck();
  }

  static getInstance(): FikoWorkerPool {
    if (!FikoWorkerPool.instance) {
      FikoWorkerPool.instance = new FikoWorkerPool();
    }
    return FikoWorkerPool.instance;
  }

  /**
   * Initialize worker threads
   */
  private initializeWorkers(): void {
    try {
      for (let i = 0; i < this.WORKER_COUNT; i++) {
        const worker = new Worker(
          new URL('../fiko-geometry.worker', import.meta.url),
          { type: 'module' }
        );

        worker.onmessage = (event) => this.handleWorkerMessage(event, i);
        worker.onerror = (error) => this.handleWorkerError(error, i);

        this.workers.push(worker);
      }
    } catch (error) {
      this.workers = [];
    }
  }

  /**
   * Process task in worker pool
   * 🚀 PERFORMANS OPT: Added priority parameter for critical path optimization
   */
  async processTask<T>(
    type: WorkerTaskType,
    data: any,
    transfer?: Transferable[],
    priority: TaskPriority = TaskPriority.NORMAL
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const task: WorkerTask = {
        id: this.taskId++,
        type,
        data,
        resolve,
        reject,
        timestamp: performance.now(),
        priority,
        retryCount: 0
      };

      // No workers available - fallback to main thread
      if (this.workers.length === 0) {
        try {
          const result = this.processTaskSync(type, data);
          resolve(result as T);
        } catch (error) {
          reject(error);
        }
        return;
      }

      // Find idle worker
      const idleWorker = this.findIdleWorker();
      if (idleWorker !== -1) {
        this.executeTask(task, idleWorker, transfer);
      } else {
        // 🚀 PERFORMANS OPT: Queue by priority
        this.priorityQueues[priority].push(task);
      }
    });
  }

  /**
   * 🚀 PERFORMANS OPT: Get next task from priority queues
   * Higher priority tasks are processed first
   */
  private getNextQueuedTask(): WorkerTask | undefined {
    for (const queue of this.priorityQueues) {
      if (queue.length > 0) {
        return queue.shift();
      }
    }
    // Fallback to legacy queue
    return this.taskQueue.shift();
  }

  /**
   * Find first idle worker (skips dead workers)
   */
  private findIdleWorker(): number {
    for (let i = 0; i < this.workers.length; i++) {
      if (!this.activeWorkers.has(i) && !this.deadWorkerSet.has(i)) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Execute task on specific worker
   * 🚀 PERFORMANS OPT: Task timeout eklendi - memory leak önleme
   */
  private executeTask(task: WorkerTask, workerIndex: number, transfer?: Transferable[]): void {
    this.activeWorkers.set(workerIndex, task);

    // 🚀 PERFORMANS OPT: Set timeout to prevent hung tasks
    const timeoutId = setTimeout(() => {
      if (this.activeWorkers.has(workerIndex)) {
        const hungTask = this.activeWorkers.get(workerIndex);
        if (hungTask && hungTask.id === task.id) {
          this.activeWorkers.delete(workerIndex);
          this.taskTimeouts.delete(task.id);
          task.reject(new Error(`Task ${task.id} timed out after ${this.TASK_TIMEOUT_MS}ms`));

          // 🚀 PERFORMANS OPT: Process next queued task by priority
          const nextTask = this.getNextQueuedTask();
          if (nextTask) {
            this.executeTask(nextTask, workerIndex);
          }
        }
      }
    }, this.TASK_TIMEOUT_MS);

    this.taskTimeouts.set(task.id, timeoutId);

    try {
      if (transfer && transfer.length > 0) {
        // DEN-4 FIX: Filter out already-detached ArrayBuffers to prevent
        // "ArrayBuffer is already detached" errors when a Float32Array view
        // was transferred but the caller still references the typed array.
        const validTransferables = transfer.filter(t => {
          if (t instanceof ArrayBuffer) {
            return t.byteLength > 0; // detached buffers have byteLength === 0
          }
          return true; // MessagePort, ImageBitmap, etc.
        });

        if (validTransferables.length > 0) {
          this.workers[workerIndex].postMessage(
            { type: task.type, data: task.data, taskId: task.id },
            validTransferables
          );
        } else {
          // All transferables were detached, send without transfer
          this.workers[workerIndex].postMessage(
            { type: task.type, data: task.data, taskId: task.id }
          );
        }
      } else {
        this.workers[workerIndex].postMessage(
          { type: task.type, data: task.data, taskId: task.id }
        );
      }
    } catch (error) {
      this.activeWorkers.delete(workerIndex);
      clearTimeout(timeoutId);
      this.taskTimeouts.delete(task.id);
      task.reject(error);
    }
  }

  /**
   * Handle worker message
   * 🚀 PERFORMANS OPT: Timeout cleanup eklendi
   */
  private handleWorkerMessage(event: MessageEvent, workerIndex: number): void {
    const data = event.data;

    // Handle heartbeat pong response
    if (data && data.type === 'pong') {
      this.handlePong(workerIndex, data.id);
      return;
    }

    const { taskId, result, error } = data;
    const task = this.activeWorkers.get(workerIndex);

    if (task && task.id === taskId) {
      this.activeWorkers.delete(workerIndex);

      // 🚀 PERFORMANS OPT: Clear timeout on successful completion
      const timeoutId = this.taskTimeouts.get(taskId);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.taskTimeouts.delete(taskId);
      }

      if (error) {
        task.reject(new Error(error));
      } else {
        task.resolve(result);
      }

      // 🚀 PERFORMANS OPT: Process queued tasks by priority
      const nextTask = this.getNextQueuedTask();
      if (nextTask) {
        this.executeTask(nextTask, workerIndex);
      }
    } else {
      // DEN-4 FIX: Worker completed a task that was already resolved (e.g. by timeout).
      // The worker is now idle - ensure we still process queued tasks.
      if (!this.activeWorkers.has(workerIndex)) {
        const nextTask = this.getNextQueuedTask();
        if (nextTask) {
          this.executeTask(nextTask, workerIndex);
        }
      }
    }
  }

  /**
   * Handle worker errors - triggers auto-restart
   */
  private handleWorkerError(error: ErrorEvent, workerIndex: number): void {
    console.warn(`[FikoWorkerPool] Worker ${workerIndex} error, triggering restart:`, error.message);
    this.restartWorker(workerIndex);
  }

  // ========================================================================
  // DEAD WORKER DETECTION & AUTO-RESTART
  // ========================================================================

  /**
   * Start periodic health check (every 30s)
   */
  private startPeriodicHealthCheck(): void {
    if (this.healthCheckTimer) return;
    this.healthCheckTimer = setInterval(() => {
      this.pingAllWorkers();
    }, this.HEALTH_CHECK_INTERVAL_MS);
  }

  /**
   * Send ping to all workers and await pong within HEARTBEAT_TIMEOUT_MS
   */
  private pingAllWorkers(): void {
    for (let i = 0; i < this.workers.length; i++) {
      if (this.deadWorkerSet.has(i) || this.pendingPings.has(i)) continue;

      const pingId = Date.now() + i;
      try {
        this.workers[i].postMessage({ type: 'ping', id: pingId });
      } catch {
        // postMessage failed - worker is dead
        console.warn(`[FikoWorkerPool] Worker ${i} failed to accept ping, marking as dead.`);
        this.restartWorker(i);
        continue;
      }

      const timer = setTimeout(() => {
        // No pong received within timeout - worker is dead
        this.pendingPings.delete(i);
        console.warn(`[FikoWorkerPool] Worker ${i} did not respond to ping within ${this.HEARTBEAT_TIMEOUT_MS}ms, restarting.`);
        this.restartWorker(i);
      }, this.HEARTBEAT_TIMEOUT_MS);

      this.pendingPings.set(i, { pingId, timer });
    }
  }

  /**
   * Handle pong response from worker
   */
  private handlePong(workerIndex: number, pongId: number): void {
    const pending = this.pendingPings.get(workerIndex);
    if (pending && pending.pingId === pongId) {
      clearTimeout(pending.timer);
      this.pendingPings.delete(workerIndex);
    }
  }

  /**
   * Restart a dead worker: terminate, recreate, re-queue pending task
   */
  private restartWorker(workerIndex: number): void {
    if (workerIndex < 0 || workerIndex >= this.workers.length) return;

    this.deadWorkerSet.add(workerIndex);

    // Collect the pending task before termination
    const pendingTask = this.activeWorkers.get(workerIndex);
    if (pendingTask) {
      this.activeWorkers.delete(workerIndex);
      // Clear its timeout
      const timeoutId = this.taskTimeouts.get(pendingTask.id);
      if (timeoutId) {
        clearTimeout(timeoutId);
        this.taskTimeouts.delete(pendingTask.id);
      }
    }

    // Clear any pending ping
    const ping = this.pendingPings.get(workerIndex);
    if (ping) {
      clearTimeout(ping.timer);
      this.pendingPings.delete(workerIndex);
    }

    // Terminate the dead worker
    try {
      this.workers[workerIndex].terminate();
    } catch { /* already dead */ }

    // Create a new worker in its place
    try {
      const newWorker = new Worker(
        new URL('../fiko-geometry.worker', import.meta.url),
        { type: 'module' }
      );
      newWorker.onmessage = (event) => this.handleWorkerMessage(event, workerIndex);
      newWorker.onerror = (error) => this.handleWorkerError(error, workerIndex);
      this.workers[workerIndex] = newWorker;

      this.deadWorkerSet.delete(workerIndex);
      this.restartCount++;

      console.warn(`[FikoWorkerPool] Worker ${workerIndex} restarted successfully (total restarts: ${this.restartCount}).`);

      // DEN-4 FIX: Re-queue the pending task only if under max retries
      // Prevents poisoned tasks from crashing workers in an infinite loop
      if (pendingTask) {
        pendingTask.retryCount++;
        if (pendingTask.retryCount <= this.MAX_TASK_RETRIES) {
          this.priorityQueues[pendingTask.priority].unshift(pendingTask);
        } else {
          console.error(`[FikoWorkerPool] Task ${pendingTask.id} (${pendingTask.type}) exceeded max retries (${this.MAX_TASK_RETRIES}), rejecting.`);
          pendingTask.reject(new Error(`Task ${pendingTask.id} failed after ${this.MAX_TASK_RETRIES} retries - possible poisoned task`));
        }
      }

      // Try to process queued tasks on the fresh worker
      const nextTask = this.getNextQueuedTask();
      if (nextTask) {
        this.executeTask(nextTask, workerIndex);
      }
    } catch (error) {
      console.error(`[FikoWorkerPool] Failed to restart worker ${workerIndex}:`, error);
      // Worker slot remains dead, deadWorkerSet keeps it excluded
    }
  }

  /**
   * Get worker pool health status
   */
  getHealth(): { total: number; alive: number; dead: number; restarted: number } {
    return {
      total: this.workers.length,
      alive: this.workers.length - this.deadWorkerSet.size,
      dead: this.deadWorkerSet.size,
      restarted: this.restartCount
    };
  }

  /**
   * Synchronous task processing fallback
   */
  private processTaskSync(type: WorkerTaskType, data: any): any {
    switch (type) {
      case 'parse-stl':
        return this.parseSTL_Sync(data.buffer);
      case 'parse-dxf':
        return this.parseDXF_Sync(data.content);
      case 'optimize-geometry':
        return this.optimizeGeometry_Sync(data.vertices, data.indices);
      case 'compress-vertices':
        return this.compressVertices_Sync(data.vertices);
      case 'compute-normals':
        return this.computeNormals_Sync(data.vertices, data.indices);
      default:
        throw new Error(`Unknown task type: ${type}`);
    }
  }

  // ========================================================================
  // SYNCHRONOUS FALLBACK IMPLEMENTATIONS
  // ========================================================================

  private parseSTL_Sync(buffer: ArrayBuffer): any {
    const uint8Array = new Uint8Array(buffer);
    const view = new DataView(buffer);

    const isBinary = !new TextDecoder('utf-8', { fatal: false })
      .decode(uint8Array.slice(0, 5))
      .toLowerCase()
      .startsWith('solid');

    if (isBinary && buffer.byteLength >= 84) {
      const triangleCount = view.getUint32(80, true);
      const vertices: number[] = [];
      const indices: number[] = [];
      const normals: number[] = [];

      let offset = 84;
      for (let i = 0; i < triangleCount; i++) {
        const nx = view.getFloat32(offset, true);
        const ny = view.getFloat32(offset + 4, true);
        const nz = view.getFloat32(offset + 8, true);
        offset += 12;

        for (let j = 0; j < 3; j++) {
          vertices.push(
            view.getFloat32(offset, true),
            view.getFloat32(offset + 4, true),
            view.getFloat32(offset + 8, true)
          );
          offset += 12;
        }

        const idx = i * 3;
        indices.push(idx, idx + 1, idx + 2);

        for (let j = 0; j < 3; j++) {
          normals.push(nx, ny, nz);
        }

        offset += 2;
      }

      return { vertices: new Float32Array(vertices), indices: new Uint32Array(indices), normals: new Float32Array(normals) };
    }

    return null;
  }

  private parseDXF_Sync(content: string): any {
    const entities: any[] = [];
    const lines = content.split('\n');
    let inEntities = false;

    for (const line of lines) {
      const trimmed = line.trim();
      if (trimmed === 'ENTITIES') {
        inEntities = true;
      } else if (inEntities && trimmed === 'ENDSEC') {
        break;
      } else if (inEntities && trimmed.match(/LINE|CIRCLE|ARC/)) {
        entities.push({ type: trimmed });
      }
    }

    return { entities, entityCount: entities.length };
  }

  private optimizeGeometry_Sync(vertices: Float32Array, indices: Uint32Array): Uint32Array {
    // Simple vertex cache optimization
    const optimized = new Uint32Array(indices.length);
    optimized.set(indices);
    return optimized;
  }

  private compressVertices_Sync(vertices: Float32Array): Uint8Array {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    let minZ = Infinity, maxZ = -Infinity;

    for (let i = 0; i < vertices.length; i += 3) {
      minX = Math.min(minX, vertices[i]);
      maxX = Math.max(maxX, vertices[i]);
      minY = Math.min(minY, vertices[i + 1]);
      maxY = Math.max(maxY, vertices[i + 1]);
      minZ = Math.min(minZ, vertices[i + 2]);
      maxZ = Math.max(maxZ, vertices[i + 2]);
    }

    const result = new Uint8Array(vertices.length / 3 * 6);
    const view = new Uint16Array(result.buffer);

    for (let i = 0; i < vertices.length; i += 3) {
      const x = Math.round(((vertices[i] - minX) / (maxX - minX)) * 65535);
      const y = Math.round(((vertices[i + 1] - minY) / (maxY - minY)) * 65535);
      const z = Math.round(((vertices[i + 2] - minZ) / (maxZ - minZ)) * 65535);

      view[(i / 3) * 3] = x;
      view[(i / 3) * 3 + 1] = y;
      view[(i / 3) * 3 + 2] = z;
    }

    return result;
  }

  private computeNormals_Sync(vertices: Float32Array, indices: Uint32Array): Float32Array {
    const normals = new Float32Array(vertices.length);

    for (let i = 0; i < indices.length; i += 3) {
      const i0 = indices[i] * 3;
      const i1 = indices[i + 1] * 3;
      const i2 = indices[i + 2] * 3;

      const v0x = vertices[i0], v0y = vertices[i0 + 1], v0z = vertices[i0 + 2];
      const v1x = vertices[i1], v1y = vertices[i1 + 1], v1z = vertices[i1 + 2];
      const v2x = vertices[i2], v2y = vertices[i2 + 1], v2z = vertices[i2 + 2];

      const e1x = v1x - v0x, e1y = v1y - v0y, e1z = v1z - v0z;
      const e2x = v2x - v0x, e2y = v2y - v0y, e2z = v2z - v0z;

      const nx = e1y * e2z - e1z * e2y;
      const ny = e1z * e2x - e1x * e2z;
      const nz = e1x * e2y - e1y * e2x;

      const len = Math.sqrt(nx * nx + ny * ny + nz * nz);
      if (len > 0) {
        const invLen = 1 / len;
        normals[i0] += nx * invLen;
        normals[i0 + 1] += ny * invLen;
        normals[i0 + 2] += nz * invLen;

        normals[i1] += nx * invLen;
        normals[i1 + 1] += ny * invLen;
        normals[i1 + 2] += nz * invLen;

        normals[i2] += nx * invLen;
        normals[i2 + 1] += ny * invLen;
        normals[i2 + 2] += nz * invLen;
      }
    }

    return normals;
  }

  /**
   * Terminate all workers
   */
  dispose(): void {
    // Stop health check timer
    if (this.healthCheckTimer) {
      clearInterval(this.healthCheckTimer);
      this.healthCheckTimer = null;
    }

    // Clear all pending pings
    for (const [, ping] of this.pendingPings) {
      clearTimeout(ping.timer);
    }
    this.pendingPings.clear();

    // Clear all task timeouts
    for (const [, timeoutId] of this.taskTimeouts) {
      clearTimeout(timeoutId);
    }
    this.taskTimeouts.clear();

    // Reject all active tasks so callers aren't left hanging
    for (const [, task] of this.activeWorkers) {
      task.reject(new Error('Worker pool disposed'));
    }

    // Reject all queued tasks
    for (const queue of this.priorityQueues) {
      for (const task of queue) {
        task.reject(new Error('Worker pool disposed'));
      }
      queue.length = 0;
    }
    for (const task of this.taskQueue) {
      task.reject(new Error('Worker pool disposed'));
    }

    this.workers.forEach(worker => worker.terminate());
    this.workers = [];
    this.activeWorkers.clear();
    this.taskQueue = [];
    this.priorityQueues = [[], [], [], []];
    this.deadWorkerSet.clear();

    // DEN-4 FIX: Null the singleton so getInstance() creates a fresh pool
    FikoWorkerPool.instance = null as any;
  }

  /**
   * Get worker pool statistics
   */
  getStats(): WorkerPoolStats {
    return {
      workerCount: this.workers.length,
      activeWorkers: this.activeWorkers.size,
      queuedTasks: this.taskQueue.length,
      totalTasks: this.taskId
    };
  }
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

// 🚀 PERFORMANS OPT: Task priority levels
export enum TaskPriority {
  CRITICAL = 0,  // Selection, hover - must respond immediately
  HIGH = 1,      // User-initiated actions
  NORMAL = 2,    // Background processing
  LOW = 3        // Optimization, cleanup
}

interface WorkerTask {
  id: number;
  type: WorkerTaskType;
  data: any;
  resolve: (result: any) => void;
  reject: (error: any) => void;
  timestamp: number;
  priority: TaskPriority;
  retryCount: number; // DEN-4 FIX: Track retries to prevent poisoned task infinite restart
}

export type WorkerTaskType =
  | 'parse-stl'
  | 'parse-dxf'
  | 'optimize-geometry'
  | 'compress-vertices'
  | 'compute-normals';

export interface WorkerPoolStats {
  workerCount: number;
  activeWorkers: number;
  queuedTasks: number;
  totalTasks: number;
}
